import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type Status = "loading" | "valid" | "already" | "invalid" | "done" | "error";

const Unsubscribe = () => {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const [status, setStatus] = useState<Status>("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    (async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`, {
          headers: { apikey: SUPABASE_ANON },
        });
        const json = await res.json();
        if (res.ok && json.valid) setStatus("valid");
        else if (json?.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch {
        setStatus("error");
      }
    })();
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", { body: { token } });
    setBusy(false);
    if (error) { setStatus("error"); return; }
    if ((data as any)?.success) setStatus("done");
    else if ((data as any)?.reason === "already_unsubscribed") setStatus("already");
    else setStatus("error");
  };

  return (
    <div className="min-h-dvh flex items-center justify-center p-6 bg-background">
      <Helmet>
        <title>Odhlášení odběru — Tipomatic</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Card className="w-full max-w-md rounded-2xl shadow-card border-border">
        <CardHeader>
          <CardTitle className="text-xl">{t("unsubscribe.title", { defaultValue: "Odhlášení odběru e-mailů" })}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "loading" && <p className="text-sm text-muted-foreground">{t("common.loading", { defaultValue: "Načítám…" })}</p>}
          {status === "valid" && (
            <>
              <p className="text-sm">{t("unsubscribe.confirm", { defaultValue: "Chceš se opravdu odhlásit ze zasílání app e-mailů?" })}</p>
              <Button disabled={busy} onClick={confirm} className="w-full h-11 rounded-full bg-gradient-hero text-primary-foreground border-0">
                {t("unsubscribe.confirmBtn", { defaultValue: "Odhlásit odběr" })}
              </Button>
            </>
          )}
          {status === "done" && <p className="text-sm">{t("unsubscribe.done", { defaultValue: "Hotovo. Už ti nepošleme žádné app e-maily." })}</p>}
          {status === "already" && <p className="text-sm">{t("unsubscribe.already", { defaultValue: "Tato adresa už je odhlášená." })}</p>}
          {status === "invalid" && <p className="text-sm text-destructive">{t("unsubscribe.invalid", { defaultValue: "Neplatný nebo expirovaný odkaz." })}</p>}
          {status === "error" && <p className="text-sm text-destructive">{t("unsubscribe.error", { defaultValue: "Něco se pokazilo, zkus to prosím později." })}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe;
